import React from 'react';
import { Flame } from 'lucide-react';
import { cn } from "../../lib/utils";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../ui/hover-card";
import { Progress } from "../ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Trophy, Zap, Award, Star } from 'lucide-react';
import { getLevelTitle } from '../../utils/gamification';

interface LevelInfo {
  level: number;
  currentXp: number;
  nextLevelXp: number;
  progressPercent: number;
  badgeColor: string;
}

interface UserRPGBadgesProps {
  user: {
    id: string;
    name?: string;
    email: string;
    streakCount?: number;
    levelInfo?: LevelInfo;
    totalExperience?: number;
    maxStreak?: number;
    totalMissionsCompleted?: number;
    totalPosts?: number;
  };
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  avatarUrl?: string;
}

export default function UserRPGBadges({ 
  user, 
  size = 'md', 
  className,
  avatarUrl
}: UserRPGBadgesProps): JSX.Element {
  const levelInfo = user.levelInfo || {
    level: 1,
    currentXp: 0,
    nextLevelXp: 50,
    progressPercent: 0,
    badgeColor: '#94a3b8'
  };

  const streakCount = user.streakCount || 0;

  const getInitials = (name?: string, email?: string) => {
    const text = name || email || '??';
    const parts = text.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return text.slice(0, 2).toUpperCase();
  };

  const statsPanel = (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative">
          <Avatar className="h-12 w-12 border-2 border-primary/50">
            <AvatarImage src={avatarUrl} />
            <AvatarFallback className="bg-muted text-primary font-bold">
              {getInitials(user.name, user.email)}
            </AvatarFallback>
          </Avatar>
          <div className="absolute -bottom-1 -right-1 bg-primary text-white text-[10px] font-black px-1.5 rounded-full border border-slate-900">
            LVL {levelInfo.level}
          </div>
        </div>
        <div>
          <h4 className="text-sm font-bold text-white">{user.name || user.email.split('@')[0]}</h4>
          <p className="text-xs text-muted-foreground italic capitalize">{getLevelTitle(levelInfo.level)}</p>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          <span>Experience</span>
          <span>{levelInfo.currentXp} / {levelInfo.nextLevelXp} XP</span>
        </div>
        <Progress value={levelInfo.progressPercent} className="h-1.5 bg-card" indicatorClassName="bg-primary" />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="bg-muted/50 border border-border p-2 rounded-lg flex flex-col items-center justify-center">
          <Award className="h-4 w-4 text-orange-400 mb-1" />
          <span className="text-[10px] text-muted-foreground uppercase font-bold">Missions</span>
          <span className="text-sm font-black text-white">{user.totalMissionsCompleted || 0}</span>
        </div>
        <div className="bg-muted/50 border border-border p-2 rounded-lg flex flex-col items-center justify-center">
          <Zap className="h-4 w-4 text-yellow-400 mb-1" />
          <span className="text-[10px] text-muted-foreground uppercase font-bold">Best Streak</span>
          <span className="text-sm font-black text-white">{user.maxStreak || streakCount}</span>
        </div>
        <div className="bg-muted/50 border border-border p-2 rounded-lg flex flex-col items-center justify-center">
          <Trophy className="h-4 w-4 text-primary mb-1" />
          <span className="text-[10px] text-muted-foreground uppercase font-bold">Total Posts</span>
          <span className="text-sm font-black text-white">{user.totalPosts || 0}</span>
        </div>
        <div className="bg-muted/50 border border-border p-2 rounded-lg flex flex-col items-center justify-center">
          <Star className="h-4 w-4 text-purple-400 mb-1" />
          <span className="text-[10px] text-muted-foreground uppercase font-bold">Global XP</span>
          <span className="text-sm font-black text-white">{user.totalExperience || levelInfo.currentXp}</span>
        </div>
      </div>
    </div>
  );

  return (
    <HoverCard openDelay={100} closeDelay={200}>
      <HoverCardTrigger asChild>
        <div className={cn("flex items-center gap-2 cursor-help", className)}>
          {/* Streak Icon */}
          {streakCount > 0 && (
            <div 
              className={cn(
                "flex items-center gap-1 bg-orange-600 rounded-full border-2 border-[#0f172a] shadow-[0_4px_12px_rgba(234,88,12,0.4)] animate-in slide-in-from-left-2 duration-300 shrink-0",
                size === 'sm' ? 'px-1.5 py-0.5' : size === 'md' ? 'px-2 py-1' : 'px-4 py-2'
              )}
            >
              <Flame className={cn("fill-white text-white", size === 'sm' ? 'h-2.5 w-2.5' : size === 'md' ? 'h-3.5 w-3.5' : 'h-6 w-6')} />
              <span className={cn("font-black text-white leading-none tracking-tighter", size === 'sm' ? 'text-[9px]' : size === 'md' ? 'text-[11px]' : 'text-xl')}>
                {streakCount}
              </span>
            </div>
          )}

          {/* Level Badge */}
          <div 
            className={cn(
              "flex items-center justify-center rounded-full border-2 font-black text-white shadow-xl px-2.5 py-0.5",
              size === 'sm' ? 'h-6 min-w-[42px] text-[10px]' : size === 'md' ? 'h-8 min-w-[54px] text-xs' : 'h-14 min-w-[80px] text-xl'
            )}
            style={{ 
              backgroundColor: '#0f172a', 
              borderColor: levelInfo.badgeColor,
              boxShadow: `0 0 10px ${levelInfo.badgeColor}40`
            }}
          >
            <span className="text-primary mr-1 font-bold uppercase tracking-widest text-[0.75em]">Lv</span>
            <span className="text-white">{levelInfo.level}</span>
          </div>
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="w-80 bg-background border-border shadow-2xl p-4 z-[100]" side="bottom" align="start">
        {statsPanel}
      </HoverCardContent>
    </HoverCard>
  );
}
